/**
 * Hesab vəziyyəti zolağı — poçt ekranında qalıcı.
 *
 * NİYƏ LAZIMDIR
 * Əvvəl panel kirayəçinin ödəniş vəziyyətindən XƏBƏRSİZ idi. Nəticəsi:
 * ödənişi olmayan müştəri normal poçt ekranı görürdü, məktub yazırdı,
 * «Göndər» basırdı — və SMTP qatından ümumi xəta alırdı. Nə baş
 * verdiyini anlamırdı və dəstəyə yazırdı.
 *
 * ⚠️ `unpaid` HALI ƏN VACİBİDİR (2026-08-08 tarifi). Pulsuz sınaq
 * ləğv ediləndən sonra hər yeni hesab bu vəziyyətdə başlayır: panel tam
 * işləyir, poçt isə HƏR İKİ istiqamətdə bağlıdır. Zolaq olmasa, müştəri
 * işləyən paneli görüb poçtun da işlədiyini güman edir və ünvanını
 * müştərilərinə paylayır — məktublar səssizcə geri dönür.
 *
 * ⚠️ ZOLAQ MÜŞTƏRİNİN OLDUĞU YERDƏ GÖRÜNMƏLİDİR, ayrıca səhifədə yox.
 * Abunə ekranında xəbərdarlıq qoymaq kifayət etmir — oraya yalnız
 * problemi ARTIQ bilən adam gedir. Yandex 360 eyni həlli işlədir:
 * hesab məhdudlaşdırılanda hər ekranın başında qalıcı zolaq durur və
 * içində DƏRHAL hərəkət düyməsi olur.
 *
 * ⚠️ NƏ İŞLƏYİR, NƏ İŞLƏMİR — AÇIQ SADALANIR.
 * «Hesabınız məhdudlaşdırılıb» cümləsi müştərini paniklədir: poçtunu
 * itirdiyini düşünüb məlumatını köçürməyə başlayır. Halbuki `past_due`
 * halında gələn poçt GƏLİR və oxunur — yalnız göndəriş dayanır. Bunu
 * demək müştərini saxlayır.
 */

import type { User } from '../types';

interface Props {
  user: User;
  /** «Abunə» bölməsinə keçid — zolağın hərəkət düyməsi. */
  onGoBilling: () => void;
}

/** Abunənin bitməsinə neçə gün qalanda xəbərdarlıq görünsün. */
const WARN_FROM_DAYS = 5;

export function AccountBanner({ user, onGoBilling }: Props): React.ReactElement | null {
  const status = user.tenantStatus;
  const days = user.subscriptionDaysLeft;

  // ⚠️ ADİ QUTU SAHİBİ ÜÇÜN DÜYMƏ GÖSTƏRİLMİR — ödəniş onun işi deyil.
  // Zolağın özü görünür (niyə göndərə bilmədiyini bilməlidir), amma
  // «Abunə seç» düyməsi yalnız şirkət administratorundadır.
  const canPay = user.isTenantAdmin;

  // ── Heç vaxt ödəməyib: poçt tam bağlıdır ──────────────────────────
  //
  // ⚠️ BİRİNCİ ŞƏRT VƏ `role="alert"` — QƏSDƏN.
  // Bu, digər hallardan CİDDİDİR: `past_due`-da poçt hələ gəlir, burada
  // heç nə gəlmir. Ekran oxuyucu istifadəçisi zolağı təsadüfən tapmasın
  // deyə `status` yox, `alert` işlədilir — sətir dərhal oxunur.
  if (status === 'unpaid') {
    return (
      <div className="acct-banner is-stop" role="alert">
        <div className="acct-text">
          <strong>Poçt hələ işləmir — ödəniş gözlənilir.</strong> Bu domenə
          məktub nə gəlir, nə də göndərilir; sizə yazanlar «çatdırılmadı»
          cavabı alır. Domeni quraşdırmaq və qutu yaratmaq indi də mümkündür —
          ödənişdən dərhal sonra poçt işə düşür.
        </div>
        {canPay && (
          <button className="acct-action" onClick={onGoBilling}>
            Abunəni aç
          </button>
        )}
      </div>
    );
  }

  if (status === 'past_due') {
    return (
      <div className="acct-banner is-stop" role="status">
        <div className="acct-text">
          <strong>Göndəriş dayandırılıb — ödəniş gözlənilir.</strong>{' '}
          Gələn məktublar hələ də qəbul olunur və köhnə yazışmalarınız yerindədir.
          Abunə rəsmiləşdirilməsə, bir həftədən sonra poçt qəbulu da dayanacaq.
        </div>
        {canPay && (
          <button className="acct-action" onClick={onGoBilling}>
            Abunəni rəsmiləşdir
          </button>
        )}
      </div>
    );
  }

  // ⚠️ `suspended` HALINDA PANELƏ GİRİŞ ÜMUMİYYƏTLƏ YOXDUR
  // (session.ts → ACTIVE_STATUSES). Bu şərt yalnız gələcəkdə siyasət
  // yumşalarsa işə düşsün deyə saxlanılır — indi ölü koddur, amma
  // yanlış «hər şey qaydasındadır» görüntüsü verməkdən yaxşıdır.
  if (status === 'suspended') {
    return (
      <div className="acct-banner is-stop" role="alert">
        <div className="acct-text">
          <strong>Hesab dayandırılıb.</strong> Poçt qəbulu və göndərişi dayanıb.
          Məktublarınız silinməyib.
        </div>
        {canPay && (
          <button className="acct-action" onClick={onGoBilling}>
            Bərpa et
          </button>
        )}
      </div>
    );
  }

  // Abunənin sonu — yalnız SONA YAXIN. Ayın əvvəlindən xəbərdarlıq
  // göstərmək zolağı «fon səs-küyünə» çevirir və bitəndə heç kim
  // ona baxmır.
  //
  // ⚠️ `daysLeft` YALNIZ `active` HALINDA DOLU OLUR (session.ts), ona
  // görə status ayrıca yoxlanmır — köhnə `trial` hesablarda da bu zolaq
  // özbaşına açılmır.
  if (days !== null && days <= WARN_FROM_DAYS) {
    return (
      <div className="acct-banner is-soon" role="status">
        <div className="acct-text">
          Abunənin bitməsinə <strong>{days} gün</strong> qalıb. Yenilənməsə,
          gələn məktublar gəlməyə davam edir — yalnız göndəriş dayanır.
        </div>
        {canPay && (
          <button className="acct-action" onClick={onGoBilling}>
            Abunəni yenilə
          </button>
        )}
      </div>
    );
  }

  return null;
}
